import { VariablesOf } from '@graphql-typed-document-node/core';
import { queryOptions } from '@tanstack/react-query';

import { notEmpty } from 'utils/typescript.utils';

import { removeLocale } from 'queries/keys.utils';
import { datoRequester, ticketRequester } from 'queries/requesters';
import { SiteLocale } from 'types/api/dato.types';
import {
  PageEventsCardsDocument,
  TicketsEventIdByDatoEventSlugDocument,
} from 'types/api/dato-operations.types';
import {
  AccountEventsOverviewDocument,
  AccountEventTicketsGroupedDocument,
  CartDocument,
  EventCategoriesDocument,
  EventDocument,
  EventLowestPriceDocument,
  EventTicketDocument,
  GeneratingTicketsDocument,
  GetTransferDocument,
  OrderDocument,
  TicketOffersByCodeDocument,
  TicketsInfoDocument,
  TransferByShareCodeDocument,
} from 'types/api/tickets.types';
import { Categories, EnhancedTicketsEventCard } from 'types/tickets.types';

import { isPastEvent, isUpcomingEvent } from 'components/@events/utils';

const TICKETS_PREFIX = 'tickets';

export const ticketsEventCategoriesQuery = (
  variables: VariablesOf<typeof EventCategoriesDocument> & {
    locale: SiteLocale;
  },
) =>
  queryOptions({
    queryKey: [TICKETS_PREFIX, 'event-categories', removeLocale(variables)],
    queryFn: async () => {
      const { event } = await ticketRequester(
        EventCategoriesDocument,
        variables,
      )();

      const mergedCategories =
        event?.categories?.reduce<Categories>((acc, category) => {
          if (category.children?.length) {
            category.children.forEach((child) => {
              const tickets = child.tickets ?? [];
              const uniqueTickets = Array.from(
                new Map(
                  tickets.map((ticket) => [ticket['id'], ticket]),
                ).values(),
              );
              acc.push({
                id: child.id,
                categoryWithSubcategoryName: [
                  category.name?.[variables.locale],
                  child.name?.[variables.locale],
                ]
                  .filter(notEmpty)
                  .join(' - '),
                name: category.name?.[variables.locale] ?? '',
                tickets: uniqueTickets,
              });
            });
          }

          return acc;
        }, []) ?? [];

      const filters = [
        ...new Set(mergedCategories.map((category) => category.name)),
      ];

      return { categories: mergedCategories, filters };
    },
  });

export const ticketsEventTicketQuery = (
  variables: VariablesOf<typeof EventTicketDocument>,
) =>
  queryOptions({
    queryKey: [TICKETS_PREFIX, 'event', 'ticket', removeLocale(variables)],
    queryFn: ticketRequester(EventTicketDocument, variables),
  });

export const ticketsEventQuery = (
  variables: VariablesOf<typeof EventDocument>,
) =>
  queryOptions({
    queryKey: [TICKETS_PREFIX, 'event', removeLocale(variables)],
    queryFn: ticketRequester(EventDocument, variables),
  });

export const ticketsInfoQuery = (
  variables: VariablesOf<typeof TicketsInfoDocument>,
) =>
  queryOptions({
    queryKey: [TICKETS_PREFIX, 'info', removeLocale(variables)],
    queryFn: ticketRequester(TicketsInfoDocument, variables),
  });

export const ticketsEventIdByDatoEventSlugQuery = (
  variables: VariablesOf<typeof TicketsEventIdByDatoEventSlugDocument>,
) =>
  queryOptions({
    queryKey: [TICKETS_PREFIX, 'dato-event', removeLocale(variables)],
    queryFn: datoRequester(TicketsEventIdByDatoEventSlugDocument, variables),
  });

export const ticketsCartQuery = (variables: VariablesOf<typeof CartDocument>) =>
  queryOptions({
    queryKey: [TICKETS_PREFIX, 'cart', removeLocale(variables)],
    queryFn: ticketRequester(CartDocument, variables),
  });

export const ticketsOrderQuery = (
  variables: VariablesOf<typeof OrderDocument>,
) =>
  queryOptions({
    queryKey: [TICKETS_PREFIX, 'order', removeLocale(variables)],
    queryFn: ticketRequester(OrderDocument, variables),
  });

export const ticketsLowestPriceQuery = (
  variables: VariablesOf<typeof EventLowestPriceDocument>,
) =>
  queryOptions({
    queryKey: [TICKETS_PREFIX, 'lowest-price', removeLocale(variables)],
    queryFn: ticketRequester(EventLowestPriceDocument, variables),
  });

export const accountTicketsGenerating = (
  variables: VariablesOf<typeof GeneratingTicketsDocument>,
) =>
  queryOptions({
    queryKey: [TICKETS_PREFIX, 'generating', variables],
    queryFn: ticketRequester(GeneratingTicketsDocument, variables),
  });

export const accountTicketsOverviewQuery = (variables: {
  locale: SiteLocale;
}) =>
  queryOptions({
    queryKey: [TICKETS_PREFIX, 'account-overview', variables],
    queryFn: async (ctx) => {
      const { accountEventsOverview } = await ticketRequester(
        AccountEventsOverviewDocument,
      )();

      const { allPageEvents } = await datoRequester(PageEventsCardsDocument, {
        ...variables,
        orderBy: 'startDatetime_DESC',
        first: 500,
        filter: {
          ticketsEventId: {
            in: accountEventsOverview.map((info) => info.id),
          },
          OR: [
            { ticketsTimeslotId: { isBlank: true } },
            {
              ticketsTimeslotId: {
                in: accountEventsOverview
                  .map((info) => info.timeslots.map((t) => t.id))
                  .flat(),
              },
            },
          ],
        },
      })(ctx);

      const enhancedEvents = allPageEvents.reduce<
        Array<EnhancedTicketsEventCard>
      >((acc, event) => {
        const matchingCountInfo = accountEventsOverview.find(
          (info) => info.id === event.ticketsEventId,
        );
        if (!matchingCountInfo) return acc;

        if (!event.ticketsTimeslotId) {
          acc.push({
            ...event,
            ticketCount: matchingCountInfo.ticketCount,
          });
        } else {
          const matchingTimeslotCount = matchingCountInfo.timeslots.find(
            (info) => info.id === event.ticketsTimeslotId,
          );

          const ticketCount = matchingTimeslotCount?.ticketCount ?? 0;

          if (ticketCount > 0) {
            acc.push({
              ...event,
              ticketCount,
            });
          }
        }
        return acc;
      }, []);

      return {
        upcomingEvents: enhancedEvents.filter(isUpcomingEvent),
        pastEvents: enhancedEvents.filter(isPastEvent),
      };
    },
  });

export const accountEventTicketsGroupedQuery = (
  variables: VariablesOf<typeof AccountEventTicketsGroupedDocument>,
) =>
  queryOptions({
    queryKey: [TICKETS_PREFIX, 'account-tickets-for-event', variables],
    queryFn: ticketRequester(AccountEventTicketsGroupedDocument, variables),
  });

export const ticketsTabbyPrescoringQuery = queryOptions({
  queryKey: [TICKETS_PREFIX, 'tabby-prescoring'],
});

export const transferByShareCodeQuery = (
  variables: VariablesOf<typeof TransferByShareCodeDocument>,
) =>
  queryOptions({
    queryKey: [TICKETS_PREFIX, 'transfer-by-shareCode', variables],
    queryFn: ticketRequester(TransferByShareCodeDocument, variables),
  });

export const transferByIdQuery = (
  variables: VariablesOf<typeof GetTransferDocument>,
) =>
  queryOptions({
    queryKey: [TICKETS_PREFIX, 'transfer', variables],
    queryFn: ticketRequester(GetTransferDocument, variables),
  });

export const ticketOffersByCodeQuery = (
  variables: VariablesOf<typeof TicketOffersByCodeDocument> & {
    locale: SiteLocale;
  },
) =>
  queryOptions({
    queryKey: [
      TICKETS_PREFIX,
      'ticket-offers-by-code',
      removeLocale(variables),
    ],
    queryFn: async () => {
      const { ticketOffersByCode } = await ticketRequester(
        TicketOffersByCodeDocument,
        variables,
      )();

      return ticketOffersByCode.reduce<Categories>((acc, offer) => {
        const tickets = offer.tickets ?? [];
        const uniqueTickets = Array.from(
          new Map(tickets.map((ticket) => [ticket['id'], ticket])).values(),
        );
        const name = offer.name?.[variables.locale] ?? '';
        acc.push({
          id: offer.id,
          name,
          categoryWithSubcategoryName: name,
          tickets: uniqueTickets,
        });
        return acc;
      }, []);
    },
  });

export const loyaltyEffectsQuery = queryOptions({
  queryKey: [TICKETS_PREFIX, 'loyalty-effects'],
});
